import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../services/GraphQLServices";
import React, {useState} from "react";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {cloneMutationData} from "../products/Utils";
import {PentaSplit} from "../../components/common/PentaSplit";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {EXISTS_MODULE, GET_MODULE, PUT_MODULE} from "./Queries";
import {Module} from "./models/Module";
import {translationsEditorTargetStage} from "./TranslationsOverview";
import {TabPanel} from "../../components/common/TabPanel";
import {translateFunction} from "../../localization/TranslationService";

const translate = translateFunction("SureBasePortalTranslations");

export const ModuleDetails = pageAuthorizedByGroup(function () {
	const id = useParams()?.id;
	
	const {loading, data, error} = useQuery(GET_MODULE, {
		client: graphQLServices.translationsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: translationsEditorTargetStage,
			id: id,
		}
	});
	
	const [module, setModule] = useState<Module>();
	
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	const nodes = data.modules.nodes;
	if (nodes === null || nodes.length === 0)
		throw new Error(`Module with id ${id} was not found.`);
	
	const node: Module = nodes[0];
	if (!module) {
		setModule(node);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	
	const props = {module, setModule};
	
	const handleSave = async (existingItem: Module) => {
		await moduleExists(existingItem).then(async (x) => {
			if (x) {
				throw new Error(`Item already exists`);
			}
			
			await saveChanges(existingItem);
		});
	};
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup">{translate("Translations management")}</Link>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup/modules">{translate("Modules")}</Link>
			<Typography color="text.primary">{id}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<Stack style={{height: "100%"}}>
				<div style={{flexGrow: 1, overflow: "hidden"}}>
					<Information {...props}/>
				</div>
				<div style={{flexGrow: 0, padding: "10px"}}>
					<ProcessButton
						defaultText={translate("Save")}
						inProgressText={translate("Saving")}
						completedText={translate("Saved")}
						resetKeys={[module]}
						variant={"outlined"}
						onClick={() => handleSave(module)}/>
				</div>
			</Stack>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function moduleExists(item: Module): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_MODULE,
			variables: {
				stage: translationsEditorTargetStage,
				name: item.name,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.modules.nodes;
	return nodes.filter(x => x.id !== item.id).length > 0;
}

async function saveChanges(module: Module) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_MODULE,
		variables: {
			stage: translationsEditorTargetStage,
			module: cloneMutationData(module),
		}
	});
}

interface ModuleProps {
	readonly module: Module;
	readonly setModule: (module: Module) => void;
}

function Information(props: ModuleProps) {
	return <TabPanel value={0} index={0}>
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2}>
				<div>{translate("Module Information")}</div>
				<TextField
					label={translate("Name")}
					value={props.module.name}
					variant={"standard"}
					onChange={event => props.setModule({...props.module, name: event.target.value})}
				/>
			</Stack>
		</div>
	</TabPanel>;
}


export function openModuleDetailsFor(id: string) {
	window.location.href = linkModuleDetailsFor(id);
}

export function linkModuleDetailsFor(id: string): string {
	return `/translations_setup/modules/${encodeURIComponent(id)}`;
}
